import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * useSwipeBack — iOS-style swipe-from-left-edge to navigate back.
 * 
 * Detects touch gestures starting within the left 25px edge of the screen,
 * and if the user swipes right far enough (>80px), triggers router.back().
 */
const useSwipeBack = () => {
    const navigate = useNavigate();
    const touchRef = useRef({ startX: 0, startY: 0, started: false });

    const EDGE_WIDTH = 25;       // Touch must start within this many px of left edge
    const MIN_SWIPE_X = 80;      // Minimum horizontal distance to trigger back
    const MAX_SWIPE_Y = 60;      // Maximum vertical drift (prevents triggering on scroll)

    const onTouchStart = useCallback((e) => {
        const touch = e.touches[0];
        if (touch.clientX <= EDGE_WIDTH) {
            touchRef.current = {
                startX: touch.clientX,
                startY: touch.clientY,
                started: true
            };
        } else {
            touchRef.current.started = false;
        }
    }, []);

    const onTouchEnd = useCallback((e) => {
        if (!touchRef.current.started) return;
        
        const touch = e.changedTouches[0];
        const dx = touch.clientX - touchRef.current.startX;
        const dy = Math.abs(touch.clientY - touchRef.current.startY);

        touchRef.current.started = false;

        if (dx >= MIN_SWIPE_X && dy <= MAX_SWIPE_Y) {
            navigate(-1);
        }
    }, [navigate]);

    useEffect(() => {
        document.addEventListener('touchstart', onTouchStart, { passive: true });
        document.addEventListener('touchend', onTouchEnd, { passive: true });

        return () => {
            document.removeEventListener('touchstart', onTouchStart);
            document.removeEventListener('touchend', onTouchEnd);
        };
    }, [onTouchStart, onTouchEnd]);
};

export default useSwipeBack;
